import * as React from 'react';
import { IconSizes, XCloseIcon } from '@newcore/icons';
import { BadgeSizes } from '../../enums/variants';
import type { BadgeColors, BadgeVariants } from '../../enums/variants';
import { cn } from '../../utils/cn';
import { badgeDotVariants, badgeVariants } from '../../variants/badge-variants';
import { Tooltip } from '../tooltips';

interface BadgeProps extends React.ComponentProps<'span'> {
  variant?: BadgeVariants;
  color?: BadgeColors;
  size?: BadgeSizes;
  hasDot?: boolean;
  onRemove?: () => void;
  disabled?: boolean;
  tooltip?: string;
}

function Badge({
  className,
  variant,
  color,
  size,
  hasDot,
  onRemove,
  disabled,
  tooltip,
  children,
  ...props
}: BadgeProps) {
  const wrappedChildren = wrapBadgeChildren(children);
  const removeButton = onRemove && (
    <button
      type='button'
      disabled={disabled}
      onClick={event => {
        event.stopPropagation();
        onRemove();
      }}
      className='inline-flex items-center justify-center rounded-sm transition-colors shrink-0 opacity-70 hover:opacity-100 hover:bg-current/10 cursor-pointer disabled:pointer-events-none disabled:opacity-40'
      aria-label='Remove'
    >
      <XCloseIcon size={IconSizes.XS} />
    </button>
  );

  return (
    <span
      data-slot='badge'
      data-size={size ?? BadgeSizes.MD}
      className={cn(
        badgeVariants({
          variant,
          color,
          size,
          hasDot,
          hasRemove: !!onRemove,
        }),
        className
      )}
      {...props}
    >
      {hasDot && (
        <span className={badgeDotVariants({ color })} aria-hidden>
          <span className='size-1.5 rounded-full bg-current' />
        </span>
      )}
      {wrappedChildren}
      {removeButton && tooltip ? (
        <Tooltip content={tooltip}>
          <span className='inline-flex'>{removeButton}</span>
        </Tooltip>
      ) : (
        removeButton
      )}
    </span>
  );
}

// Wrap text so `:first-child` / `:only-child` selectors match in DOM, and merge
// adjacent strings/numbers into one <span> so the gap utility renders no space
// between e.g. `{value}/10`.
function wrapBadgeChildren(children: React.ReactNode): React.ReactNode[] {
  const childArray = React.Children.toArray(children);
  const result: React.ReactNode[] = [];
  let textBuffer = '';

  const flushText = () => {
    if (textBuffer.length === 0) {
      return;
    }
    result.push(<span key={`badge-text-${result.length}`}>{textBuffer}</span>);
    textBuffer = '';
  };

  for (const child of childArray) {
    if (typeof child === 'string' || typeof child === 'number') {
      textBuffer += String(child);
      continue;
    }
    flushText();
    result.push(child);
  }
  flushText();

  return result;
}

export { Badge };
